import React, { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, Megaphone, ArrowRight } from 'lucide-react';
import {
  DEFAULT_PROMOTIONS_CONFIG,
  type PromotionsConfig,
} from '../editor/components/PromotionsManagerDrawer';

export interface PublicPromotionsProps {
  className?: string;
  initialConfig?: Partial<PromotionsConfig>;
}

export function PublicPromotions({
  className = '',
  initialConfig,
}: PublicPromotionsProps): React.JSX.Element | null {
  const [config, setConfig] = useState<PromotionsConfig>(() => {
    let base = DEFAULT_PROMOTIONS_CONFIG;
    try {
      const stored = localStorage.getItem('helios_site_promotions');
      if (stored) {
        base = { ...DEFAULT_PROMOTIONS_CONFIG, ...JSON.parse(stored) };
      }
    } catch {
      // Use default
    }
    if (initialConfig) {
      base = {
        noticeBanner: { ...base.noticeBanner, ...(initialConfig.noticeBanner || {}) },
        popupModal: { ...base.popupModal, ...(initialConfig.popupModal || {}) },
      };
    }
    return base;
  });

  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('helios_dismiss_banner') === 'true';
    } catch {
      return false;
    }
  });

  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [popupDismissed, setPopupDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('helios_dismiss_popup') === 'true';
    } catch {
      return false;
    }
  });

  // Listen for storage or custom events when promotions are updated in studio
  useEffect(() => {
    const handleUpdate = () => {
      try {
        const stored = localStorage.getItem('helios_site_promotions');
        if (stored) {
          setConfig({ ...DEFAULT_PROMOTIONS_CONFIG, ...JSON.parse(stored) });
        }
      } catch {
        // Ignored
      }
    };

    window.addEventListener('storage', handleUpdate);
    window.addEventListener('helios_promotions_updated', handleUpdate);
    return () => {
      window.removeEventListener('storage', handleUpdate);
      window.removeEventListener('helios_promotions_updated', handleUpdate);
    };
  }, []);

  // Popup Timer
  useEffect(() => {
    if (!config.popupModal?.enabled || popupDismissed) {
      return;
    }

    const delayMs = Math.max(0, config.popupModal.delaySeconds ?? 3) * 1000;
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [config.popupModal?.enabled, config.popupModal?.delaySeconds, popupDismissed]);

  // Keyboard Escape listener for popup dismissal (M-I7)
  const dismissPopup = useCallback(() => {
    setShowPopup(false);
    setPopupDismissed(true);
    try {
      sessionStorage.setItem('helios_dismiss_popup', 'true');
    } catch {
      // Ignored
    }
  }, []);

  useEffect(() => {
    if (!showPopup) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismissPopup();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPopup, dismissPopup]);

  const dismissBanner = () => {
    setBannerDismissed(true);
    try {
      sessionStorage.setItem('helios_dismiss_banner', 'true');
    } catch {
      // Ignored
    }
  };

  const banner = config.noticeBanner;
  const popup = config.popupModal;

  const getBannerStyleClasses = (style?: 'primary' | 'warning' | 'gradient' | 'dark') => {
    switch (style) {
      case 'warning':
        return 'bg-amber-500 text-slate-950 font-medium';
      case 'gradient':
        return 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-medium';
      case 'dark':
        return 'bg-slate-900 text-slate-100 border-b border-slate-800 font-medium';
      case 'primary':
      default:
        return 'bg-primary text-primary-foreground font-medium';
    }
  };

  const showBanner = banner?.enabled && !bannerDismissed;

  return (
    <div className={`promotions-layer ${className}`}>
      {/* ─────────────────────────────────────────────────────────────────────────
          1. TOP NOTICE BANNER
         ───────────────────────────────────────────────────────────────────────── */}
      {showBanner && (
        <div
          data-testid="public-notice-banner"
          className={`relative z-50 w-full px-4 py-2 text-xs transition-all shadow-xs ${getBannerStyleClasses(
            banner.style
          )}`}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <div className="flex-1 flex items-center justify-center gap-2 flex-wrap text-center">
              <span>{banner.headline}</span>
              {banner.ctaText && banner.ctaLink && (
                <a
                  href={banner.ctaLink}
                  className="font-bold underline underline-offset-2 hover:opacity-85 transition-opacity inline-flex items-center gap-0.5 cursor-pointer ml-1"
                >
                  <span>{banner.ctaText}</span>
                  <ArrowRight className="h-3 w-3" />
                </a>
              )}
            </div>

            {banner.dismissible && (
              <button
                type="button"
                onClick={dismissBanner}
                aria-label="Dismiss notice banner"
                title="Dismiss banner"
                className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors shrink-0 cursor-pointer min-h-[36px] min-w-[36px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                data-testid="dismiss-notice-banner-btn"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────
          2. DELAYED PROMOTIONAL POPUP MODAL
         ───────────────────────────────────────────────────────────────────────── */}
      {showPopup && popup?.enabled && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="promo-popup-heading"
          data-testid="public-popup-modal"
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={dismissPopup}
        >
          <div
            className="bg-card border border-border text-foreground rounded-2xl max-w-md w-full p-6 sm:p-7 shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ambient Gradient Glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

            {/* Close Button (Meets M-I8: min 44px touch target) */}
            <button
              type="button"
              onClick={dismissPopup}
              aria-label="Close promotional offer"
              title="Close offer"
              className="absolute top-4 right-4 p-2 rounded-xl border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
              data-testid="dismiss-popup-modal-btn"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Urgency Badge */}
            {popup.badge && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-3 border border-primary/20">
                <Sparkles className="h-3.5 w-3.5" />
                <span>{popup.badge}</span>
              </div>
            )}

            {/* Offer Heading */}
            <h3
              id="promo-popup-heading"
              className="text-lg sm:text-xl font-black text-foreground tracking-tight mb-2 pr-8"
            >
              {popup.heading}
            </h3>

            {/* Offer Body Copy */}
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
              {popup.offerCopy}
            </p>

            {/* Action CTA & Secondary Dismiss */}
            <div className="flex flex-col gap-2.5">
              <a
                href={popup.buttonLink || '/home#enquiry'}
                onClick={dismissPopup}
                className="w-full py-3 px-4 rounded-xl bg-primary text-primary-foreground font-bold text-xs sm:text-sm text-center shadow-lg hover:opacity-90 transition-opacity cursor-pointer flex items-center justify-center gap-2 min-h-[44px]"
                data-testid="popup-action-btn"
              >
                <span>{popup.buttonText || 'Claim Offer'}</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <button
                type="button"
                onClick={dismissPopup}
                className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer text-center"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
